'use client';

import { useEffect } from 'react';

import { isValidMetaPixelId } from '@/lib/analytics';
import { WEBSITE_LINK_MARKER_GLOBAL } from '@/lib/constants';
import { sendWebsiteLinkSignal } from '@/services/tracking';
import useTenant from '@/state/tenant';

declare global {
  interface Window {
    [WEBSITE_LINK_MARKER_GLOBAL]?: string;
  }
}

const MARKED_TAB_KEY = 'website_link_marked';

// Module-level, so StrictMode's double effect and a remount cannot send twice
// for one page load.
let handled = false;

// A reload or Back keeps the old referrer but not the marker, and would read
// as an unmarked visit from the website.
const isFreshNavigation = (): boolean => {
  const entry = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  return entry?.type === 'navigate';
};

// Exact origin comparison: another site only, http(s) only.
const otherSiteReferrerOrigin = (): string | null => {
  try {
    const referrer = new URL(document.referrer);
    if (referrer.protocol !== 'http:' && referrer.protocol !== 'https:') {
      return null;
    }
    return referrer.origin === window.location.origin ? null : referrer.origin;
  } catch {
    return null;
  }
};

const readTabMarked = (): boolean => {
  try {
    return sessionStorage.getItem(MARKED_TAB_KEY) === '1';
  } catch {
    return false;
  }
};

const markTab = (): void => {
  try {
    sessionStorage.setItem(MARKED_TAB_KEY, '1');
  } catch {}
};

// Feeds the Website links status light in the admin's Meta Pixel drawer: a
// marked landing proves the studio's website code works, an unmarked landing
// from the same website says it may have gone missing.
export const WebsiteLinkSignal = () => {
  const { data: tenant } = useTenant();
  // The same gate the fbc capture uses; it also keeps not-live studios, which
  // the public API refuses, from sending anything.
  const hasPixel = isValidMetaPixelId(tenant?.tracking_config?.meta_pixel_id);

  useEffect(() => {
    if (!hasPixel || handled) return;
    handled = true;
    if (!isFreshNavigation()) return;

    const marker = window[WEBSITE_LINK_MARKER_GLOBAL] || null;
    const referrer_origin = otherSiteReferrerOrigin();
    if (!marker && !referrer_origin) return;

    // A tab that arrived marked has proven the code works; a later unmarked
    // landing in it is a copied or rebuilt link, not a missing script.
    if (marker) markTab();
    else if (readTabMarked()) return;

    void sendWebsiteLinkSignal({
      marker,
      has_fbclid: !!new URLSearchParams(window.location.search).get('fbclid'),
      referrer_origin,
    });
  }, [hasPixel]);

  return null;
};
